import { useEffect, useRef, useState } from 'react';
import { ScanLine, PackagePlus, CheckCircle2, AlertTriangle, Loader2, Info } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import { fechaExacta } from '../ui/tiempo';
import { useCambiosSinGuardar } from '../ui/navegacionReact';
import {
  UNIDADES, NOMBRE_UNIDAD, NOMBRE_MAX, SKU_MAX, CODIGO_MAX,
  limpiarCodigo, limpiarSku, skuPorOmision,
  problemaDelCodigo, problemaDelNombre, problemaDelSku,
  aplicarSugerencia, estadoDeSugerencia, insigniaDe, origenDelCodigo,
} from './alta';

/* =====================================================================
   DAR DE ALTA DESDE EL CÓDIGO · U-03 fase a (DISEÑO-U-03 §6.1)
   =====================================================================
   Se entra desde el 404 de Escanear («Darlo de alta») y desde «Códigos que
   TEGO no reconoció» («Crear un producto con este código»), con el código
   en `contexto.codigo` (y `contexto.id_codigo` si viene de la cola). Desde
   el menú, sin código, la pantalla lo pide primero.

   LA SUGERENCIA SOLO LLENA LO QUE NADIE HA TOCADO (D18). Se lee de la copia
   local del catálogo compartido (`GET /api/gtin/sugerencia`); si llega
   después de que la persona escribió, no pisa nada. Lo que se guarda es lo
   que está en el campo. Esta pantalla solo CREA un producto o le AÑADE un
   código a uno que ya existe: nunca cambia el nombre de un producto.

   Guardar usa contratos que ya existían, sin cambio: `POST /api/productos`
   y luego `POST /api/productos/{sku}/codigos` o, desde la cola,
   `POST /api/qr/codigos-por-resolver/{id}/asignar`.
   ===================================================================== */
export default function AltaDesdeCodigo({ contexto, irA }) {
  const [codigo, setCodigo] = useState(() => limpiarCodigo(contexto?.codigo));
  const [tecleado, setTecleado] = useState(false);

  if (!codigo || problemaDelCodigo(codigo)) {
    return (
      <PedirCodigo
        inicial={codigo}
        irA={irA}
        onCodigo={(c) => { setTecleado(true); setCodigo(c); }}
      />
    );
  }
  return (
    <Formulario
      key={codigo}
      codigo={codigo}
      idCodigo={contexto?.id_codigo ?? null}
      origen={tecleado ? 'TECLEADO' : origenDelCodigo(contexto?.medio)}
      irA={irA}
    />
  );
}

function Encabezado({ subtitulo }) {
  return (
    <div className="space-y-2">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Dar de alta desde el código</h2>
        <p className="text-sm text-gray-500 mt-0.5">{subtitulo}</p>
      </div>
      <Ayuda id="gtin.alta.pantalla" />
    </div>
  );
}

// ---------------------------------------------------------------------
// Sin código: se pide primero. Es lo que ve quien entra desde el menú.
// ---------------------------------------------------------------------
function PedirCodigo({ inicial, onCodigo, irA }) {
  const [valor, setValor] = useState(inicial || '');
  const [tocado, setTocado] = useState(Boolean(inicial));
  const problema = problemaDelCodigo(valor);

  return (
    <div className="space-y-4" data-gtin-alta="pedir-codigo">
      <Encabezado subtitulo="Escanea o escribe el código de barras del producto que quieres dar de alta." />
      <form
        className="space-y-3"
        onSubmit={(e) => { e.preventDefault(); setTocado(true); if (!problema) onCodigo(limpiarCodigo(valor)); }}
      >
        <label htmlFor="gtin-codigo" className="block text-sm font-semibold text-gray-700">
          Código de barras<span className="text-red-600 ml-0.5" aria-hidden="true">*</span>
        </label>
        <input
          id="gtin-codigo"
          className="entrada font-mono"
          value={valor}
          maxLength={CODIGO_MAX + 20}
          autoFocus
          autoComplete="off"
          inputMode="text"
          placeholder="Ej. 7501234567890"
          onChange={(e) => { setValor(e.target.value); setTocado(true); }}
        />
        {tocado && problema && (
          <p role="alert" className="text-xs text-red-700">{problema}</p>
        )}
        <button
          type="submit"
          disabled={Boolean(problema)}
          aria-describedby={problema ? 'gtin-codigo-motivo' : undefined}
          className="w-full min-h-12 inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] disabled:opacity-60 text-white font-semibold rounded-xl px-4"
        >
          <PackagePlus className="h-5 w-5" aria-hidden="true" /> Seguir con este código
        </button>
        {problema && (
          <p id="gtin-codigo-motivo" className="text-xs text-gray-600">{problema}</p>
        )}
      </form>
      {irA && (
        <button
          type="button"
          onClick={() => irA('escanear')}
          className="w-full min-h-11 inline-flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-800 font-semibold rounded-xl px-4"
        >
          <ScanLine className="h-5 w-5" aria-hidden="true" /> Escanear con la cámara
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Con código: la sugerencia, el formulario mínimo y el resultado.
// ---------------------------------------------------------------------
function motivo(err, enCasoDeRed) {
  if (err instanceof ErrorApi) return err.message;
  return enCasoDeRed;
}

function Formulario({ codigo, idCodigo, origen, irA }) {
  const [campos, setCampos] = useState(() => ({
    nombre: '', sku: skuPorOmision(codigo), unidad: 'PIEZA',
  }));
  // LA MARCA DE «TOCADO». Vive en una referencia, no en el estado: la lee la
  // promesa de la sugerencia cuando llega, y tiene que ver lo último que hizo
  // la persona aunque React no haya vuelto a pintar todavía.
  const tocados = useRef({ nombre: false, unidad: false });
  const [visto, setVisto] = useState({ nombre: false, sku: false });

  const [respuesta, setRespuesta] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [lento, setLento] = useState(false);
  const [errorSugerencia, setErrorSugerencia] = useState(null);

  const [fase, setFase] = useState('FORMULARIO');          // FORMULARIO · A_MEDIAS · LISTO
  const [guardando, setGuardando] = useState(false);
  const [errorGuardar, setErrorGuardar] = useState(null);
  const [skuRepetido, setSkuRepetido] = useState(false);
  const [errorCodigo, setErrorCodigo] = useState(null);
  const [producto, setProducto] = useState(null);
  const [mensajeListo, setMensajeListo] = useState(null);

  useCambiosSinGuardar(() => fase !== 'LISTO' && campos.nombre.trim() !== '');

  useEffect(() => {
    let vigente = true;
    // El indicador sale si la consulta pasa de 300 ms; el formulario se
    // puede llenar mientras.
    const reloj = setTimeout(() => { if (vigente) setLento(true); }, 300);
    api.get(`/gtin/sugerencia?codigo=${encodeURIComponent(codigo)}`)
      .then((r) => {
        if (!vigente) return;
        setRespuesta(r);
        setCampos((c) => aplicarSugerencia(c, tocados.current, r?.sugerencia));
      })
      .catch((e) => { if (vigente) setErrorSugerencia(motivo(e, 'La consulta no llegó al servidor.')); })
      .finally(() => { clearTimeout(reloj); if (vigente) setCargando(false); });
    return () => { vigente = false; clearTimeout(reloj); };
  }, [codigo]);

  const cambiar = (clave) => (e) => {
    const valor = clave === 'sku' ? limpiarSku(e.target.value) : e.target.value;
    if (clave in tocados.current) tocados.current[clave] = true;
    setCampos((c) => ({ ...c, [clave]: valor }));
  };

  // EL CÓDIGO YA ES DE OTRO PRODUCTO (auditoría U-03 a, H8). La sugerencia
  // lo dice en `ya_es_de`, y se avisa ANTES de crear nada: crear el producto
  // y luego no poder ponerle el código dejaba un duplicado sin código.
  const yaEsDe = respuesta?.ya_es_de || null;
  const problemaNombre = problemaDelNombre(campos.nombre);
  const problemaSku = problemaDelSku(campos.sku);
  const problema = (yaEsDe && `Este código ya es de ${yaEsDe.sku}. Abre ese producto o escanea otro código.`)
    || problemaNombre || problemaSku;
  const { estado, catalogo } = estadoDeSugerencia(respuesta, { cargando, error: errorSugerencia });
  const sugerencia = estado === 'CON' ? respuesta.sugerencia : null;
  const propuesta = sugerencia ? String(sugerencia.nombre_propuesto || sugerencia.nombre || '').slice(0, NOMBRE_MAX) : '';
  const insignia = insigniaDe(sugerencia);

  async function ponerCodigo(prod) {
    setGuardando(true);
    setErrorCodigo(null);
    try {
      if (idCodigo !== null && idCodigo !== undefined) {
        const r = await api.post(`/qr/codigos-por-resolver/${encodeURIComponent(idCodigo)}/asignar`,
          { id_producto: prod.id });
        setMensajeListo(r?.mensaje || null);
      } else {
        await api.post(`/productos/${encodeURIComponent(prod.sku)}/codigos`, { codigo, origen });
        setMensajeListo(null);
      }
      setFase('LISTO');
    } catch (err) {
      setErrorCodigo(motivo(err, 'La petición no llegó al servidor. Revisa la conexión y vuelve a tocar «Añadir el código».'));
      setFase('A_MEDIAS');
    } finally {
      setGuardando(false);
    }
  }

  async function guardar(e) {
    e.preventDefault();
    setVisto({ nombre: true, sku: true });
    if (problema || guardando) return;
    setGuardando(true);
    setErrorGuardar(null);
    setSkuRepetido(false);
    // Se vuelve a preguntar justo antes de crear: alguien pudo ponerle el
    // código a otro producto mientras se llenaba el formulario. Si la
    // pregunta no llega, vale lo que se supo al abrir la pantalla.
    try {
      const otra = await api.get(`/gtin/sugerencia?codigo=${encodeURIComponent(codigo)}`);
      if (otra?.ya_es_de) {
        setRespuesta(otra);
        setGuardando(false);
        return;
      }
    } catch { /* se sigue con lo que se supo al abrir */ }
    let prod;
    try {
      // EL NOMBRE ES EL DEL CAMPO. No el sugerido: el del campo (prueba 16a).
      prod = await api.post('/productos', {
        sku: limpiarSku(campos.sku), nombre: campos.nombre.trim(), unidad_medida: campos.unidad,
      });
    } catch (err) {
      setErrorGuardar(motivo(err, 'La petición no llegó al servidor. Revisa la conexión y vuelve a tocar «Guardar el producto».'));
      setSkuRepetido(err instanceof ErrorApi && err.status === 409);
      setGuardando(false);
      return;
    }
    setProducto(prod);
    await ponerCodigo(prod);
  }

  async function usarExistente() {
    setGuardando(true);
    setErrorGuardar(null);
    try {
      const prod = await api.get(`/productos/${encodeURIComponent(limpiarSku(campos.sku))}`);
      setSkuRepetido(false);
      setProducto(prod);
      await ponerCodigo(prod);
    } catch (err) {
      setErrorGuardar(motivo(err, 'La petición no llegó al servidor. Revisa la conexión y vuelve a intentarlo.'));
      setGuardando(false);
    }
  }

  if (fase === 'LISTO' && producto) {
    return (
      <div className="space-y-4" data-gtin-alta="listo">
        <Encabezado subtitulo="El código ya va puesto; revisa el nombre y guarda." />
        <div role="status" className="flex items-start gap-2 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-900">
          <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />
          <p className="min-w-0 break-words">
            {mensajeListo || (
              <>Listo. Escanear <span className="font-mono break-all">{codigo}</span> abre {producto.nombre}.</>
            )}
          </p>
        </div>
        {irA && (
          <div className="grid gap-2">
            <button
              type="button"
              onClick={() => irA('escanear')}
              className="w-full min-h-12 inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] text-white font-semibold rounded-xl px-4"
            >
              <ScanLine className="h-5 w-5" aria-hidden="true" /> Escanear otro
            </button>
            <button
              type="button"
              onClick={() => irA('productos', { sku: producto.sku })}
              className="w-full min-h-11 inline-flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-800 font-semibold rounded-xl px-4"
            >
              Abrir la ficha
            </button>
            {idCodigo !== null && idCodigo !== undefined && (
              <button
                type="button"
                onClick={() => irA('codigos-por-resolver')}
                className="w-full min-h-11 inline-flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-800 font-semibold rounded-xl px-4"
              >
                Volver a la lista de códigos
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  if (fase === 'A_MEDIAS' && producto) {
    return (
      <div className="space-y-4" data-gtin-alta="a-medias">
        <Encabezado subtitulo="El código ya va puesto; revisa el nombre y guarda." />
        <div role="alert" className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="flex items-start gap-2 font-semibold">
            <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span className="min-w-0 break-words">
              El producto quedó creado pero sin el código. Toca «Añadir el código» para terminar.
            </span>
          </p>
          {errorCodigo && <p className="min-w-0 break-words">{errorCodigo}</p>}
          <p className="min-w-0 break-words text-amber-800">
            {producto.nombre} · <span className="font-mono break-all">{producto.sku}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={() => ponerCodigo(producto)}
          disabled={guardando}
          title={guardando ? 'Añadiendo el código…' : undefined}
          className="w-full min-h-12 inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] disabled:opacity-60 text-white font-semibold rounded-xl px-4"
        >
          {guardando ? 'Añadiendo…' : 'Añadir el código'}
        </button>
        {irA && (
          <button
            type="button"
            onClick={() => irA('productos', { sku: producto.sku })}
            className="w-full min-h-11 inline-flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-800 font-semibold rounded-xl px-4"
          >
            Abrir la ficha
          </button>
        )}
      </div>
    );
  }

  return (
    <form className="space-y-4" onSubmit={guardar} noValidate data-gtin-alta="formulario">
      <Encabezado subtitulo="El código ya va puesto; revisa el nombre y guarda." />

      {/* EL CÓDIGO, SOLO LECTURA. Un código de 120 caracteres sin espacios se
          parte dentro de su caja: a 320 px no empuja la página de lado. */}
      <div>
        <span className="block text-sm font-semibold text-gray-700 mb-1">Código</span>
        <div data-gtin-codigo className="rounded border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-base text-gray-900 break-all">
          {codigo}
        </div>
      </div>

      {yaEsDe && (
        <div role="alert" data-gtin-ya-es-de className="space-y-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <p className="flex items-start gap-2 font-semibold">
            <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span className="min-w-0 break-words">
              Este código ya es de {yaEsDe.nombre} (<span className="font-mono break-all">{yaEsDe.sku}</span>).
            </span>
          </p>
          <p className="min-w-0 break-words">
            Un código apunta a un solo producto: aquí no se crea otro con él. Abre ese producto para revisarlo, o escanea otro código.
          </p>
          {irA && (
            <div className="grid gap-2">
              <button
                type="button"
                onClick={() => irA('productos', { sku: yaEsDe.sku })}
                className="w-full min-h-11 inline-flex items-center justify-center gap-2 rounded-xl border border-red-300 bg-white px-3 font-semibold text-red-800"
              >
                Abrir el producto que ya lo tiene
              </button>
              <button
                type="button"
                onClick={() => irA('escanear')}
                className="w-full min-h-11 inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-3 font-semibold text-gray-800"
              >
                <ScanLine className="h-5 w-5" aria-hidden="true" /> Escanear otro código
              </button>
            </div>
          )}
        </div>
      )}

      <EstadoDeLaSugerencia
        estado={estado}
        catalogo={catalogo}
        lento={lento}
        porque={respuesta?.porque}
        actualizadoEn={respuesta?.catalogo?.actualizado_en}
        error={errorSugerencia}
      />

      <div>
        <label htmlFor="gtin-nombre" className="block text-sm font-semibold text-gray-700 mb-1">
          Nombre<span className="text-red-600 ml-0.5" aria-hidden="true">*</span>
        </label>
        <input
          id="gtin-nombre"
          className={`entrada ${visto.nombre && problemaNombre ? 'border-red-400' : ''}`}
          value={campos.nombre}
          onChange={cambiar('nombre')}
          onBlur={() => setVisto((v) => ({ ...v, nombre: true }))}
          aria-invalid={visto.nombre && problemaNombre ? 'true' : undefined}
          autoComplete="off"
        />
        {visto.nombre && problemaNombre && (
          <span role="alert" className="block text-xs text-red-700 mt-1">{problemaNombre}</span>
        )}
        {insignia && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span
              data-insignia={insignia.color}
              className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${
                insignia.color === 'verde'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-amber-50 border border-amber-200 text-amber-900'}`}
            >
              {insignia.texto}
            </span>
          </div>
        )}
        {sugerencia && campos.nombre !== propuesta && (
          <div className="mt-2 space-y-1">
            <p className="text-sm text-gray-700 min-w-0 break-words">
              Nombre sugerido: <strong>{propuesta}</strong>
            </p>
            <button
              type="button"
              onClick={() => { tocados.current.nombre = true; setCampos((c) => ({ ...c, nombre: propuesta })); }}
              className="min-h-11 inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-800"
            >
              Usar el nombre sugerido
            </button>
          </div>
        )}
        <Ayuda id="gtin.alta.nombre" />
        {sugerencia && <Ayuda id="gtin.alta.sugerencia" />}
      </div>

      <div>
        <label htmlFor="gtin-sku" className="block text-sm font-semibold text-gray-700 mb-1">
          SKU<span className="text-red-600 ml-0.5" aria-hidden="true">*</span>
        </label>
        <input
          id="gtin-sku"
          className={`entrada font-mono ${visto.sku && problemaSku ? 'border-red-400' : ''}`}
          value={campos.sku}
          maxLength={SKU_MAX + 20}
          onChange={cambiar('sku')}
          onBlur={() => setVisto((v) => ({ ...v, sku: true }))}
          aria-invalid={visto.sku && problemaSku ? 'true' : undefined}
          autoCapitalize="characters"
          autoComplete="off"
        />
        {visto.sku && problemaSku && (
          <span role="alert" className="block text-xs text-red-700 mt-1">{problemaSku}</span>
        )}
        <Ayuda id="gtin.alta.sku" />
      </div>

      <div>
        <label htmlFor="gtin-unidad" className="block text-sm font-semibold text-gray-700 mb-1">Unidad</label>
        <select id="gtin-unidad" className="entrada" value={campos.unidad} onChange={cambiar('unidad')}>
          {UNIDADES.map((u) => <option key={u} value={u}>{NOMBRE_UNIDAD[u]}</option>)}
        </select>
      </div>

      {errorGuardar && (
        <div role="alert" className="space-y-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <p className="min-w-0 break-words">{errorGuardar}</p>
          {skuRepetido && (
            <>
              <p className="min-w-0 break-words">Cambia el SKU de arriba, o usa el producto que ya tiene ese SKU.</p>
              <button
                type="button"
                onClick={usarExistente}
                disabled={guardando}
                title={guardando ? 'Guardando…' : undefined}
                className="w-full min-h-11 inline-flex items-center justify-center gap-2 rounded-xl border border-red-300 bg-white px-3 font-semibold text-red-800 disabled:opacity-60"
              >
                Usar el producto que ya existe
              </button>
              <Ayuda id="gtin.alta.usar_existente" />
            </>
          )}
        </div>
      )}

      <div>
        <button
          type="submit"
          disabled={Boolean(problema) || guardando}
          aria-describedby={problema ? 'gtin-alta-motivo' : undefined}
          className="w-full min-h-12 inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] disabled:opacity-60 text-white font-semibold rounded-xl px-4"
        >
          {guardando
            ? <><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Guardando…</>
            : <><PackagePlus className="h-5 w-5" aria-hidden="true" /> Guardar el producto</>}
        </button>
        {problema && !guardando && (
          <p id="gtin-alta-motivo" className="mt-1 text-xs text-gray-600">{problema}</p>
        )}
      </div>

      <details className="rounded-xl border border-gray-200 bg-white p-3">
        <summary className="min-h-11 flex items-center gap-2 cursor-pointer text-sm font-semibold text-gray-700">
          <Info className="h-4 w-4 shrink-0" aria-hidden="true" /> De dónde sale el nombre sugerido
        </summary>
        <Ayuda id="gtin.alta.ficha" />
      </details>
    </form>
  );
}

// ---------------------------------------------------------------------
// Lo que se sabe de la sugerencia, en una caja de una o dos líneas.
// Ninguno de estos estados impide guardar.
// ---------------------------------------------------------------------
function EstadoDeLaSugerencia({ estado, catalogo, lento, porque, actualizadoEn, error }) {
  if (estado === 'CARGANDO') {
    if (!lento) return null;
    return (
      <p role="status" data-sugerencia="cargando" className="flex items-center gap-2 text-sm text-gray-600">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden="true" />
        Buscando el nombre en la lista de este servidor… Puedes escribir mientras.
      </p>
    );
  }
  if (estado === 'ERROR') {
    return (
      <p role="status" data-sugerencia="error" className="rounded border border-red-200 bg-red-50 p-2.5 text-sm text-red-800 min-w-0 break-words">
        La lista de nombres no contestó: {error} Escribe el nombre como lo conoces; el alta funciona igual.
      </p>
    );
  }
  if (estado === 'NO_COMPARTIBLE') {
    return (
      <div data-sugerencia="no-compartible" className="rounded border border-gray-200 bg-gray-50 p-2.5 text-sm text-gray-700 min-w-0 break-words">
        <p>{porque || 'Este código es de uso interno de una tienda o de un cupón; no se busca en el catálogo compartido.'}</p>
        <Ayuda id="gtin.alta.no_compartible" />
      </div>
    );
  }
  const nota = catalogo === 'SIN_COPIA'
    ? 'Este servidor no tiene la lista de nombres de otras tiendas. El alta funciona igual: escribe el nombre.'
    : catalogo === 'VIEJA'
      ? `La lista de nombres de este servidor se actualizó el ${fechaExacta(actualizadoEn)}. El alta funciona igual.`
      : null;
  return (
    <div className="space-y-2">
      {estado === 'SIN' && catalogo !== 'SIN_COPIA' && (
        <p data-sugerencia="sin" className="rounded border border-gray-200 bg-gray-50 p-2.5 text-sm text-gray-700">
          Este código no está en el catálogo compartido. Escribe el nombre como lo conoces.
        </p>
      )}
      {nota && (
        <p data-sugerencia="catalogo" className="rounded border border-amber-200 bg-amber-50 p-2.5 text-sm text-amber-900 min-w-0 break-words">
          {nota}
        </p>
      )}
    </div>
  );
}
